import { useCallback, useEffect, useMemo, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';

import { useAppDispatch, useAppSelector } from '@store/store.tsx';

import {
  markTemplateDraftSaved,
  resetTemplateDraft,
  selectTemplateDraftEntry,
  selectTemplateDraftDock,
  upsertTemplateDraft,
  setActiveTemplateDraft,
  setTemplateDraftDockMinimized,
  type TemplateDraftEntry,
} from '../state/templateDraftSlice';
import {
  resolveCatalogueTemplate,
  applyTemplateDefinitionOverride,
  type TemplateDefinitionOverrides,
} from '../resolveCatalogueTemplate';
import {
  parseTemplateDefinition,
  TemplateSchemaError,
  type CatalogueTemplateDefinition,
  type CatalogueTemplateRegion,
  type LayoutOverrides,
  type TemplateSlotEntry,
  type TemplateSlotList,
} from '../schema';
import { getBlockRegistryForDesign } from '../blocks';
import { createDefinitionApplier } from '../applyTemplateDefinition';
import type { VisualTemplate } from '../system/templateTypes';
import { parseAspectValue } from '../visual/shared';

interface UseTemplateDraftOptions {
  template: string | null | undefined;
  design: string | null | undefined;
  baseDefinition?: CatalogueTemplateDefinition | null;
  publishedOverrides?: TemplateDefinitionOverrides | null;
}

export interface DraftValidationResult {
  ok: boolean;
  error?: Error;
  issues?: ReadonlyArray<string> | null;
}

export interface UseTemplateDraftResult {
  template: string | null;
  design: string | null;
  draftKey: string | null;
  isLoading: boolean;
  error: Error | null;
  slots: Record<string, TemplateSlotList> | null;
  baseDefinition: CatalogueTemplateDefinition | null;
  previewDefinition: CatalogueTemplateDefinition | null;
  draftEntry: TemplateDraftEntry | undefined;
  availableBlocks: {
    header: string[];
    panel: string[];
    card: string[];
  };
  addBlock: (slotKey: string, block: string, index?: number) => void;
  removeBlock: (slotKey: string, entryKey: string) => void;
  moveBlock: (slotKey: string, fromIndex: number, toIndex: number) => void;
  updateEntry: (slotKey: string, entryKey: string, next: TemplateSlotEntry) => void;
  saveDraft: (timestamp?: string | null) => void;
  resetDraft: () => void;
  validateDraft: () => DraftValidationResult;
  applyToTemplate: (template: VisualTemplate) => VisualTemplate;
  isMinimized: boolean;
  setDockMinimized: (minimized: boolean) => void;
  sections: {
    available: TemplateSectionKey[];
    visibility: Record<TemplateSectionKey, boolean>;
    toggle: (section: TemplateSectionKey, visible: boolean) => void;
  };
  style: {
    heroAspectClass: string | null;
    heroAspectDefaultRatio: string | null;
    setHeroAspectClass: (value: string | null) => void;
    bannerAspectClass: string | null;
    bannerAspectDefaultRatio: string;
    setBannerAspectClass: (value: string | null) => void;
    cardAspectClass: string | null;
    setCardAspectClass: (value: string | null) => void;
    itemCardSize: string | null;
    setItemCardSize: (value: string | null) => void;
  };
  panel: {
    topFiltersVisible: boolean;
    setTopFiltersVisible: (visible: boolean) => void;
    footerVisible: boolean;
    setFooterVisible: (visible: boolean) => void;
    storeHeroVisible: boolean;
    setStoreHeroVisible: (visible: boolean) => void;
    topBannerPosition: 'grid' | 'hero';
    setTopBannerPosition: (position: 'grid' | 'hero') => void;
  };
}

let generatedKeyCounter = 0;

const generateEntryKey = (slotKey: string, block: string) =>
  `${slotKey}-${block}-${Date.now().toString(36)}-${(generatedKeyCounter++).toString(36)}`;

export const SECTION_ORDER = ['storeHero', 'hero', 'header', 'panel', 'sidebar', 'footer'] as const;

export type TemplateSectionKey = (typeof SECTION_ORDER)[number];

const SECTION_REGION_MAP: Record<TemplateSectionKey, CatalogueTemplateRegion> = {
  storeHero: 'storeHero',
  hero: 'hero',
  header: 'header',
  panel: 'panel',
  sidebar: 'aside',
  footer: 'footer',
};

const PANEL_LAYOUT_KEY = 'panel';
const TOP_FILTERS_TOKEN = '--catalogue-toolbar-display';
const PANEL_FOOTER_TOKEN = '--catalogue-footer-visibility';
const STORE_HERO_TOKEN = '--catalogue-store-hero-visibility';
const ITEM_CARD_SIZE_TOKEN = '--catalogue-item-card-size';
const TOP_BANNER_POSITION_TOKEN = '--catalogue-top-banner-position';
export const PANEL_TOKEN_DEFAULTS = {
  [TOP_FILTERS_TOKEN]: 'flex',
  [PANEL_FOOTER_TOKEN]: 'block',
  [STORE_HERO_TOKEN]: 'block',
  [ITEM_CARD_SIZE_TOKEN]: 'normal',
  [TOP_BANNER_POSITION_TOKEN]: 'grid',
} as const;
const BANNER_ASPECT_TOKEN = '--catalogue-banner-aspect';
const DEFAULT_BANNER_RATIO_LABEL = '5:1';
type PanelTokenKey = keyof typeof PANEL_TOKEN_DEFAULTS;
const HIDDEN_TOKEN_VALUE = 'none';

export const deriveSectionAvailability = (
  regions: ReadonlyArray<CatalogueTemplateRegion>,
): TemplateSectionKey[] =>
  SECTION_ORDER.filter((section) => regions.includes(SECTION_REGION_MAP[section]));

const cloneCondition = (condition: TemplateSlotEntry['when']): TemplateSlotEntry['when'] => {
  if (!condition || typeof condition === 'string') {
    return condition ?? undefined;
  }

  if ('all' in condition || 'any' in condition || 'not' in condition) {
    const cloned: TemplateSlotEntry['when'] = {};
    if (condition.all) {
      cloned.all = condition.all.map((child) => cloneCondition(child) as any);
    }
    if (condition.any) {
      cloned.any = condition.any.map((child) => cloneCondition(child) as any);
    }
    if (condition.not) {
      cloned.not = cloneCondition(condition.not) as any;
    }
    return cloned;
  }

  return condition;
};

const cloneSlotEntry = (entry: TemplateSlotEntry, prefix: string): TemplateSlotEntry => {
  const key = entry.key && entry.key.trim() ? entry.key : generateEntryKey(prefix, entry.block);
  return {
    block: entry.block,
    key,
    when: cloneCondition(entry.when),
    area: typeof entry.area === 'string' ? entry.area : undefined,
    props: entry.props ? { ...entry.props } : undefined,
    slots: entry.slots ? cloneSlotRecord(entry.slots, `${prefix}-${key}`) : undefined,
  };
};

const cloneSlotRecord = (
  slots: Record<string, TemplateSlotList>,
  prefix: string,
): Record<string, TemplateSlotList> => {
  const cloned: Record<string, TemplateSlotList> = {};
  Object.entries(slots).forEach(([slotKey, slotList]) => {
    cloned[slotKey] = slotList.map((entry) => cloneSlotEntry(entry, `${prefix}-${slotKey}`));
  });
  return cloned;
};

const cloneLayoutOverride = (value: LayoutOverrides | undefined): LayoutOverrides => {
  const source = value ?? {};
  const cloned: LayoutOverrides = {};

  if (source.className !== undefined) {
    cloned.className = source.className;
  }
  if (source.fullWidth !== undefined) {
    cloned.fullWidth = source.fullWidth;
  }
  if (source.ratio !== undefined) {
    cloned.ratio = source.ratio;
  }
  if (source.style) {
    cloned.style = { ...source.style };
  }
  if (source.tokens) {
    cloned.tokens = { ...source.tokens };
  }
  if (source.grid) {
    cloned.grid = { ...source.grid };
  }

  return cloned;
};

const isEmptyLayoutOverride = (value: LayoutOverrides): boolean => {
  const hasClassName = typeof value.className === 'string' && value.className.trim().length > 0;
  const hasFullWidth = typeof value.fullWidth !== 'undefined';
  const hasRatio = typeof value.ratio !== 'undefined';
  const hasStyle = value.style ? Object.keys(value.style).length > 0 : false;
  const hasTokens = value.tokens ? Object.keys(value.tokens).length > 0 : false;
  const hasGrid = value.grid ? Object.keys(value.grid).length > 0 : false;

  return !hasClassName && !hasFullWidth && !hasRatio && !hasStyle && !hasTokens && !hasGrid;
};

const cloneLayoutOverridesRecord = (
  layout: Record<string, LayoutOverrides> | null | undefined,
): Record<string, LayoutOverrides> => {
  if (!layout) {
    return {};
  }

  const cloned: Record<string, LayoutOverrides> = {};
  Object.entries(layout).forEach(([key, value]) => {
    cloned[key] = cloneLayoutOverride(value);
  });
  return cloned;
};

interface PanelTokenVisibilityOptions {
  layoutOverrides: Record<string, LayoutOverrides> | null | undefined;
  tokenKey: PanelTokenKey;
  shouldShow: boolean;
  isHidden: boolean;
}

interface PanelTokenVisibilityResult {
  layout: Record<string, LayoutOverrides> | null;
  didChange: boolean;
}

/**
 * @internal Exported para pruebas de regresión del mapeo Hero/Footer.
 */
export const applyPanelTokenVisibility = ({
  layoutOverrides,
  tokenKey,
  shouldShow,
  isHidden,
}: PanelTokenVisibilityOptions): PanelTokenVisibilityResult => {
  if ((shouldShow && !isHidden) || (!shouldShow && isHidden)) {
    return { layout: layoutOverrides ?? null, didChange: false };
  }

  const nextLayout = cloneLayoutOverridesRecord(layoutOverrides);
  const currentPanel = cloneLayoutOverride(layoutOverrides?.[PANEL_LAYOUT_KEY]);

  if (shouldShow) {
    if (!currentPanel.tokens || typeof currentPanel.tokens[tokenKey] === 'undefined') {
      return { layout: layoutOverrides ?? null, didChange: false };
    }

    const nextTokens = { ...currentPanel.tokens };
    delete nextTokens[tokenKey];

    if (Object.keys(nextTokens).length > 0) {
      currentPanel.tokens = nextTokens;
    } else {
      delete currentPanel.tokens;
    }

    if (isEmptyLayoutOverride(currentPanel)) {
      delete nextLayout[PANEL_LAYOUT_KEY];
    } else {
      nextLayout[PANEL_LAYOUT_KEY] = currentPanel;
    }

    const hasEntries = Object.keys(nextLayout).length > 0;
    return { layout: hasEntries ? nextLayout : null, didChange: true };
  }

  const nextTokens = { ...(currentPanel.tokens ?? {}) };
  nextTokens[tokenKey] = HIDDEN_TOKEN_VALUE;
  currentPanel.tokens = nextTokens;
  nextLayout[PANEL_LAYOUT_KEY] = currentPanel;

  return { layout: nextLayout, didChange: true };
};

const extractAspectClassName = (className: string | undefined): string | null => {
  if (!className) {
    return null;
  }
  const match = className.match(/\baspect_\d+-\d+\b/i);
  return match ? match[0] : null;
};

const normalizeRatioLabel = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const slashMatch = trimmed.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (slashMatch) {
    return `${slashMatch[1]}:${slashMatch[2]}`;
  }

  const colonMatch = trimmed.match(/^(\d+)\s*:\s*(\d+)$/);
  if (colonMatch) {
    return `${colonMatch[1]}:${colonMatch[2]}`;
  }

  return trimmed;
};

const splitClassNames = (value: string | undefined | null): string[] => {
  if (!value) {
    return [];
  }
  return value
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);
};

const withoutAspectTokens = (tokens: string[]): string[] =>
  tokens.filter((token) => !/^aspect_\d+-\d+$/i.test(token));

const normalizeDefinition = (
  definition: CatalogueTemplateDefinition,
  prefix: string,
): CatalogueTemplateDefinition => ({
  ...definition,
  layout: definition.layout ? { ...definition.layout } : undefined,
  slots: cloneSlotRecord(definition.slots, prefix),
});

const normalizeSlots = (
  slots: Record<string, TemplateSlotList>,
  prefix: string,
): Record<string, TemplateSlotList> => cloneSlotRecord(slots, prefix);

export const useTemplateDraft = ({
  template,
  design,
  baseDefinition,
  publishedOverrides,
}: UseTemplateDraftOptions): UseTemplateDraftResult => {
  const dispatch = useAppDispatch();
  const draftKey = template && design ? `${template}:${design}` : null;
  const draftEntry = useAppSelector((state) => (draftKey ? selectTemplateDraftEntry(state, draftKey) : undefined));
  const dockState = useAppSelector(selectTemplateDraftDock);

  const [resolvedBaseDefinition, setResolvedBaseDefinition] = useState<CatalogueTemplateDefinition | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);

  useEffect(() => {
    if (!template || !design) {
      setResolvedBaseDefinition(null);
      setLoadError(null);
      setIsLoading(false);
      return;
    }

    const keyPrefix = `${template}:${design}`;

    if (baseDefinition) {
      setResolvedBaseDefinition(normalizeDefinition(baseDefinition, keyPrefix));
      setLoadError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    resolveCatalogueTemplate(template, {
      definitionDesign: design,
      publishedOverrides: publishedOverrides ?? undefined,
    })
      .then(({ definition }) => {
        if (cancelled) {
          return;
        }
        setResolvedBaseDefinition(normalizeDefinition(definition, keyPrefix));
        setLoadError(null);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        setResolvedBaseDefinition(null);
        setLoadError(error instanceof Error ? error : new Error(String(error)));
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [template, design, baseDefinition, publishedOverrides]);

  const slots = useMemo(() => {
    if (draftEntry?.overrides?.slots) {
      return draftEntry.overrides.slots;
    }
    return resolvedBaseDefinition?.slots ?? null;
  }, [draftEntry?.overrides?.slots, resolvedBaseDefinition?.slots]);

  const baseRegions = resolvedBaseDefinition?.regions ?? [];
  const overrideRegions = draftEntry?.overrides?.regions ?? publishedOverrides?.regions ?? null;
  const layoutOverrides = draftEntry?.overrides?.layout ?? publishedOverrides?.layout ?? null;
  const panelLayoutOverride = layoutOverrides?.[PANEL_LAYOUT_KEY] ?? null;
  const panelTokens = panelLayoutOverride?.tokens ?? null;
  const bannerAspectClass = useMemo(() => {
    const tokenValue = panelTokens?.[BANNER_ASPECT_TOKEN];
    if (typeof tokenValue !== 'string') {
      return null;
    }

    const normalized = normalizeRatioLabel(tokenValue);
    if (!normalized) {
      return null;
    }

    const [width, height] = normalized.split(':');
    if (!width || !height) {
      return null;
    }

    return `aspect_${width}-${height}`;
  }, [panelTokens]);
  const bannerAspectDefaultRatio = useMemo(() => {
    const basePanelTokens = resolvedBaseDefinition?.layout?.panel?.tokens;
    if (basePanelTokens) {
      const normalized = normalizeRatioLabel(basePanelTokens[BANNER_ASPECT_TOKEN]);
      if (normalized) {
        return normalized;
      }
    }

    return DEFAULT_BANNER_RATIO_LABEL;
  }, [resolvedBaseDefinition?.layout?.panel?.tokens]);
  const topFiltersHidden = useMemo(() => {
    if (!panelTokens) {
      return false;
    }
    const tokenValue = panelTokens[TOP_FILTERS_TOKEN];
    if (typeof tokenValue !== 'string') {
      return false;
    }
    return tokenValue.trim().toLowerCase() === HIDDEN_TOKEN_VALUE;
  }, [panelTokens]);
  const footerHidden = useMemo(() => {
    if (!panelTokens) {
      return false;
    }
    const tokenValue = panelTokens[PANEL_FOOTER_TOKEN];
    if (typeof tokenValue !== 'string') {
      return false;
    }
    return tokenValue.trim().toLowerCase() === HIDDEN_TOKEN_VALUE;
  }, [panelTokens]);
  const storeHeroHidden = useMemo(() => {
    if (!panelTokens) {
      return false;
    }
    const tokenValue = panelTokens[STORE_HERO_TOKEN];
    if (typeof tokenValue !== 'string') {
      return false;
    }
    return tokenValue.trim().toLowerCase() === HIDDEN_TOKEN_VALUE;
  }, [panelTokens]);
  const topBannerPosition = useMemo((): 'grid' | 'hero' => {
    if (!panelTokens) {
      return 'grid';
    }
    const tokenValue = panelTokens[TOP_BANNER_POSITION_TOKEN];
    if (typeof tokenValue !== 'string') {
      return 'grid';
    }
    const normalized = tokenValue.trim().toLowerCase();
    return normalized === 'hero' ? 'hero' : 'grid';
  }, [panelTokens]);
  const baseHeroClassTokens = useMemo(
    () => withoutAspectTokens(splitClassNames(resolvedBaseDefinition?.layout?.hero?.className)),
    [resolvedBaseDefinition?.layout?.hero?.className],
  );
  const effectiveRegions = useMemo(() => {
    if (!resolvedBaseDefinition) {
      return null;
    }
    if (!overrideRegions) {
      return baseRegions;
    }
    return overrideRegions.filter((region) => baseRegions.includes(region));
  }, [overrideRegions, baseRegions, resolvedBaseDefinition]);

  const availableSectionKeys = useMemo(
    () => [
      // storeHero always available first (UI-only section, not tied to template regions)
      'storeHero' as TemplateSectionKey,
      ...deriveSectionAvailability(baseRegions).filter(
        (sectionKey) => sectionKey !== 'header' && sectionKey !== 'panel' && sectionKey !== 'storeHero',
      ),
    ],
    [baseRegions],
  );

  const sectionsVisibility = useMemo(() => {
    const visibility: Record<TemplateSectionKey, boolean> = {
      storeHero: !storeHeroHidden,
      hero: false,
      header: false,
      panel: false,
      sidebar: false,
      footer: false,
    };
    const regionSet = new Set(effectiveRegions ?? []);
    SECTION_ORDER.forEach((section) => {
      if (section === 'storeHero') {
        // Already set above from storeHeroHidden
        return;
      }
      const region = SECTION_REGION_MAP[section];
      if (section === 'footer') {
        visibility[section] = regionSet.has(region) && !footerHidden;
        return;
      }
      visibility[section] = regionSet.has(region);
    });
    return visibility;
  }, [effectiveRegions, footerHidden, storeHeroHidden]);

  const heroAspectClass = useMemo(
    () => extractAspectClassName(layoutOverrides?.hero?.className),
    [layoutOverrides?.hero?.className],
  );

  const heroAspectDefaultRatio = useMemo(() => {
    const layoutRatio = resolvedBaseDefinition?.layout?.hero?.ratio;
    if (layoutRatio) {
      return normalizeRatioLabel(layoutRatio);
    }

    const heroSlots = resolvedBaseDefinition?.slots?.hero ?? [];
    for (const entry of heroSlots) {
      const props = entry.props as { ratio?: unknown } | undefined;
      if (!props || typeof props.ratio === 'undefined') {
        continue;
      }
      const ratioProp = props.ratio;
      if (typeof ratioProp === 'string') {
        const normalized = normalizeRatioLabel(ratioProp);
        if (normalized) {
          return normalized;
        }
      } else if (ratioProp && typeof ratioProp === 'object') {
        const ratioRecord = ratioProp as Record<string, unknown>;
        const preferred =
          normalizeRatioLabel(ratioRecord.desktop) ?? normalizeRatioLabel(ratioRecord.mobile);
        if (preferred) {
          return preferred;
        }
      }
    }

    return null;
  }, [resolvedBaseDefinition?.layout?.hero?.ratio, resolvedBaseDefinition?.slots?.hero]);

  const previewDefinition = useMemo(() => {
    if (!resolvedBaseDefinition) {
      return null;
    }
    const overrides = draftEntry?.overrides;
    if (!overrides || Object.keys(overrides).length === 0) {
      return resolvedBaseDefinition;
    }
    return applyTemplateDefinitionOverride(resolvedBaseDefinition, overrides);
  }, [resolvedBaseDefinition, draftEntry?.overrides]);

  const commitSlots = useCallback(
    (nextSlots: Record<string, TemplateSlotList>) => {
      if (!draftKey || !template || !design) {
        return;
      }
      const normalized = normalizeSlots(nextSlots, draftKey);
      dispatch(upsertTemplateDraft({ key: draftKey, template, design, slots: normalized }));
    },
    [dispatch, draftKey, template, design],
  );

  const commitRegions = useCallback(
    (nextRegions: CatalogueTemplateRegion[] | null) => {
      if (!draftKey || !template || !design) {
        return;
      }
      dispatch(
        upsertTemplateDraft({
          key: draftKey,
          template,
          design,
          regions: nextRegions ? [...nextRegions] : null,
        }),
      );
    },
    [dispatch, draftKey, template, design],
  );

  const commitLayout = useCallback(
    (nextLayout: Record<string, LayoutOverrides> | null) => {
      if (!draftKey || !template || !design) {
        return;
      }

      const sanitized = nextLayout && Object.keys(nextLayout).length > 0 ? nextLayout : null;
      dispatch(
        upsertTemplateDraft({
          key: draftKey,
          template,
          design,
          layout: sanitized,
        }),
      );
    },
    [dispatch, draftKey, template, design],
  );

  const cardAspectClass = useMemo(
    () => extractAspectClassName(layoutOverrides?.card?.className),
    [layoutOverrides?.card?.className],
  );

  const setCardAspectClass = useCallback(
    (value: string | null) => {
      const nextLayout = cloneLayoutOverridesRecord(layoutOverrides);
      const currentCard = cloneLayoutOverride(nextLayout.card);
      const currentClasses = splitClassNames(currentCard.className);
      const nextClasses = withoutAspectTokens(currentClasses);
      if (value) {
        nextClasses.push(value);
      }
      if (nextClasses.length > 0) {
        currentCard.className = nextClasses.join(' ');
      } else {
        delete currentCard.className;
      }

      if (isEmptyLayoutOverride(currentCard)) {
        delete nextLayout.card;
      } else {
        nextLayout.card = currentCard;
      }
      commitLayout(Object.keys(nextLayout).length > 0 ? nextLayout : null);
    },
    [layoutOverrides, commitLayout],
  );

  const setItemCardSize = useCallback(
    (value: string | null) => {
      const nextLayout = cloneLayoutOverridesRecord(layoutOverrides);
      const currentPanel = cloneLayoutOverride(nextLayout[PANEL_LAYOUT_KEY]);
      const nextTokens = { ...(currentPanel.tokens ?? {}) };

      if (!value) {
        delete nextTokens[ITEM_CARD_SIZE_TOKEN];
      } else {
        nextTokens[ITEM_CARD_SIZE_TOKEN] = value;
      }

      if (Object.keys(nextTokens).length > 0) {
        currentPanel.tokens = nextTokens;
      } else {
        delete currentPanel.tokens;
      }

      if (isEmptyLayoutOverride(currentPanel)) {
        delete nextLayout[PANEL_LAYOUT_KEY];
      } else {
        nextLayout[PANEL_LAYOUT_KEY] = currentPanel;
      }

      commitLayout(Object.keys(nextLayout).length > 0 ? nextLayout : null);
    },
    [layoutOverrides, commitLayout],
  );

  const addBlock = useCallback(
    (slotKey: string, block: string, index?: number) => {
      if (!slots) {
        return;
      }
      const currentList = slots[slotKey] ?? [];
      const nextEntry = cloneSlotEntry({ block, key: generateEntryKey(slotKey, block) }, `${slotKey}`);
      const nextList = [...currentList];
      if (typeof index === 'number' && index >= 0 && index <= nextList.length) {
        nextList.splice(index, 0, nextEntry);
      } else {
        nextList.push(nextEntry);
      }
      const nextSlots = { ...slots, [slotKey]: nextList };
      commitSlots(nextSlots);
    },
    [slots, commitSlots],
  );

  const removeBlock = useCallback(
    (slotKey: string, entryKey: string) => {
      if (!slots) {
        return;
      }
      const currentList = slots[slotKey] ?? [];
      const nextList = currentList.filter((entry) => entry.key !== entryKey);
      if (nextList.length === currentList.length) {
        return;
      }
      const nextSlots = { ...slots, [slotKey]: nextList };
      commitSlots(nextSlots);
    },
    [slots, commitSlots],
  );

  const moveBlock = useCallback(
    (slotKey: string, fromIndex: number, toIndex: number) => {
      if (!slots) {
        return;
      }
      const list = slots[slotKey] ?? [];
      if (fromIndex === toIndex || fromIndex < 0 || fromIndex >= list.length) {
        return;
      }
      const boundedToIndex = Math.max(0, Math.min(list.length - 1, toIndex));
      const reordered = arrayMove(list, fromIndex, boundedToIndex).map((entry) => cloneSlotEntry(entry, `${slotKey}`));
      const nextSlots = { ...slots, [slotKey]: reordered };
      commitSlots(nextSlots);
    },
    [slots, commitSlots],
  );

  const updateEntry = useCallback(
    (slotKey: string, entryKey: string, next: TemplateSlotEntry) => {
      if (!slots) {
        return;
      }
      const list = slots[slotKey] ?? [];
      const index = list.findIndex((entry) => entry.key === entryKey);
      if (index === -1) {
        return;
      }
      const normalized = cloneSlotEntry(next, `${slotKey}`);
      const nextList = [...list];
      nextList[index] = normalized;
      const nextSlots = { ...slots, [slotKey]: nextList };
      commitSlots(nextSlots);
    },
    [slots, commitSlots],
  );

  const saveDraft = useCallback(
    (timestamp?: string | null) => {
      if (!draftKey) {
        return;
      }
      dispatch(
        markTemplateDraftSaved({
          key: draftKey,
          timestamp: timestamp ?? undefined,
        }),
      );
    },
    [dispatch, draftKey],
  );

  const resetDraftHandler = useCallback(() => {
    if (!draftKey) {
      return;
    }
    dispatch(resetTemplateDraft({ key: draftKey }));
  }, [dispatch, draftKey]);

  const validateDraft = useCallback((): DraftValidationResult => {
    if (!previewDefinition) {
      return { ok: false, error: new Error('No hay definición disponible para validar.') };
    }
    try {
      parseTemplateDefinition(previewDefinition, {
        source: `${template ?? 'template'}:${design ?? 'design'}:draft`,
      });
      return { ok: true };
    } catch (error) {
      if (error instanceof TemplateSchemaError) {
        return { ok: false, error, issues: error.issues ?? null };
      }
      return { ok: false, error: error as Error };
    }
  }, [previewDefinition, template, design]);

  const availableBlocks = useMemo(() => {
    const registry = getBlockRegistryForDesign(design);
    return {
      header: Object.keys(registry.header),
      panel: Object.keys(registry.panel),
      card: Object.keys(registry.card),
    };
  }, [design]);

  const previewApplier = useMemo(
    () => createDefinitionApplier(previewDefinition, { displayNameSuffix: 'Draft' }),
    [previewDefinition],
  );

  const applyToTemplate = useCallback(
    (visualTemplate: VisualTemplate): VisualTemplate =>
      previewApplier ? previewApplier(visualTemplate) : visualTemplate,
    [previewApplier],
  );

  const setPanelFooterVisible = useCallback(
    (visible: boolean) => {
      const { didChange, layout } = applyPanelTokenVisibility({
        layoutOverrides,
        tokenKey: PANEL_FOOTER_TOKEN,
        shouldShow: visible,
        isHidden: footerHidden,
      });

      if (!didChange) {
        return;
      }

      commitLayout(layout);
    },
    [layoutOverrides, footerHidden, commitLayout],
  );

  const setStoreHeroVisible = useCallback(
    (visible: boolean) => {
      const { didChange, layout } = applyPanelTokenVisibility({
        layoutOverrides,
        tokenKey: STORE_HERO_TOKEN,
        shouldShow: visible,
        isHidden: storeHeroHidden,
      });

      if (!didChange) {
        return;
      }

      commitLayout(layout);
    },
    [layoutOverrides, storeHeroHidden, commitLayout],
  );
  const toggleSection = useCallback(
    (section: TemplateSectionKey, visible: boolean) => {
      if (!resolvedBaseDefinition) {
        return;
      }

      // Handle UI-only sections that don't have template regions
      if (section === 'storeHero') {
        setStoreHeroVisible(visible);
        return;
      }

      const region = SECTION_REGION_MAP[section];
      if (!baseRegions.includes(region)) {
        return;
      }

      if (section === 'footer') {
        setPanelFooterVisible(visible);
        return;
      }

      const baseOrder = baseRegions;
      const current = overrideRegions ?? baseOrder;
      const nextSet = new Set(current.filter((item) => baseOrder.includes(item)));

      if (visible) {
        nextSet.add(region);
      } else {
        nextSet.delete(region);
      }

      const nextRegions = baseOrder.filter((item) => nextSet.has(item));
      const previousEffective = overrideRegions ?? baseOrder;

      const unchanged =
        previousEffective.length === nextRegions.length &&
        previousEffective.every((value, index) => value === nextRegions[index]);

      if (unchanged) {
        return;
      }

      const equalsBase =
        nextRegions.length === baseOrder.length &&
        nextRegions.every((value, index) => value === baseOrder[index]);

      commitRegions(equalsBase ? null : nextRegions);
    },
    [
      baseRegions,
      overrideRegions,
      resolvedBaseDefinition,
      commitRegions,
      setPanelFooterVisible,
      setStoreHeroVisible,
    ],
  );

  const setBannerAspectClass = useCallback(
    (nextValue: string | null) => {
      const trimmed = nextValue?.trim() ?? '';
      const currentToken = layoutOverrides?.[PANEL_LAYOUT_KEY]?.tokens?.[BANNER_ASPECT_TOKEN];

      if (!trimmed) {
        if (typeof currentToken === 'undefined') {
          return;
        }

        const nextLayout = cloneLayoutOverridesRecord(layoutOverrides);
        const nextPanel = cloneLayoutOverride(layoutOverrides?.[PANEL_LAYOUT_KEY]);

        if (nextPanel.tokens) {
          delete nextPanel.tokens[BANNER_ASPECT_TOKEN];
          if (Object.keys(nextPanel.tokens).length === 0) {
            delete nextPanel.tokens;
          }
        }

        if (isEmptyLayoutOverride(nextPanel)) {
          delete nextLayout[PANEL_LAYOUT_KEY];
        } else {
          nextLayout[PANEL_LAYOUT_KEY] = nextPanel;
        }

        commitLayout(Object.keys(nextLayout).length > 0 ? nextLayout : null);
        return;
      }

      const parsed = parseAspectValue(trimmed) as string | undefined;
      if (!parsed) {
        return;
      }

      if (currentToken === parsed) {
        return;
      }

      const nextLayout = cloneLayoutOverridesRecord(layoutOverrides);
      const nextPanel = cloneLayoutOverride(layoutOverrides?.[PANEL_LAYOUT_KEY]);
      const nextTokens = { ...(nextPanel.tokens ?? {}) };
      nextTokens[BANNER_ASPECT_TOKEN] = parsed;
      nextPanel.tokens = nextTokens;
      nextLayout[PANEL_LAYOUT_KEY] = nextPanel;
      commitLayout(nextLayout);
    },
    [commitLayout, layoutOverrides],
  );

  const setHeroAspectClass = useCallback(
    (nextValue: string | null) => {
      const trimmed = nextValue?.trim() ?? '';
      const nextLayout = cloneLayoutOverridesRecord(layoutOverrides);

      if (!trimmed) {
        if (!layoutOverrides?.hero) {
          return;
        }

        const nextHero = cloneLayoutOverride(layoutOverrides.hero);
        const preservedTokens = withoutAspectTokens(splitClassNames(nextHero.className));
        if (preservedTokens.length > 0) {
          nextHero.className = preservedTokens.join(' ');
        } else {
          delete nextHero.className;
        }

        const hasOtherProps =
          nextHero.className !== undefined ||
          nextHero.fullWidth !== undefined ||
          nextHero.ratio !== undefined ||
          (nextHero.style && Object.keys(nextHero.style).length > 0) ||
          (nextHero.tokens && Object.keys(nextHero.tokens).length > 0) ||
          (nextHero.grid && Object.keys(nextHero.grid).length > 0);

        if (hasOtherProps) {
          nextLayout.hero = nextHero;
          commitLayout(nextLayout);
        } else {
          delete nextLayout.hero;
          commitLayout(Object.keys(nextLayout).length > 0 ? nextLayout : null);
        }
        return;
      }

      const normalized = trimmed;
      const baseTokens = new Set(baseHeroClassTokens);
      const overrideTokens = withoutAspectTokens(splitClassNames(layoutOverrides?.hero?.className));
      overrideTokens.forEach((token) => baseTokens.add(token));
      const combinedTokens = Array.from(baseTokens);
      combinedTokens.push(normalized);
      const nextClassName = combinedTokens.join(' ');

      const currentClassName = layoutOverrides?.hero?.className?.trim();
      if (currentClassName === nextClassName) {
        return;
      }
      const nextHero = cloneLayoutOverride(layoutOverrides?.hero);
      nextHero.className = nextClassName;
      nextLayout.hero = nextHero;
      commitLayout(nextLayout);
    },
    [baseHeroClassTokens, commitLayout, layoutOverrides],
  );

  const setTopFiltersVisible = useCallback(
    (visible: boolean) => {
      const { didChange, layout } = applyPanelTokenVisibility({
        layoutOverrides,
        tokenKey: TOP_FILTERS_TOKEN,
        shouldShow: visible,
        isHidden: topFiltersHidden,
      });

      if (!didChange) {
        return;
      }

      commitLayout(layout);
    },
    [commitLayout, layoutOverrides, topFiltersHidden],
  );

  const setTopBannerPosition = useCallback(
    (position: 'grid' | 'hero') => {
      if (position === topBannerPosition) {
        return;
      }

      const nextLayout = cloneLayoutOverridesRecord(layoutOverrides);
      const currentPanel = cloneLayoutOverride(nextLayout[PANEL_LAYOUT_KEY]);
      const nextTokens = { ...(currentPanel.tokens ?? {}) };

      if (position === 'grid') {
        // Reset to default by removing the token
        delete nextTokens[TOP_BANNER_POSITION_TOKEN];
      } else {
        nextTokens[TOP_BANNER_POSITION_TOKEN] = position;
      }

      if (Object.keys(nextTokens).length > 0) {
        currentPanel.tokens = nextTokens;
      } else {
        delete currentPanel.tokens;
      }

      if (isEmptyLayoutOverride(currentPanel)) {
        delete nextLayout[PANEL_LAYOUT_KEY];
      } else {
        nextLayout[PANEL_LAYOUT_KEY] = currentPanel;
      }

      commitLayout(Object.keys(nextLayout).length > 0 ? nextLayout : null);
    },
    [commitLayout, layoutOverrides, topBannerPosition],
  );

  const setDockMinimized = useCallback(
    (minimized: boolean) => {
      dispatch(
        setTemplateDraftDockMinimized({
          minimized,
          key: draftKey,
          template: template ?? null,
          design: design ?? null,
        }),
      );
    },
    [dispatch, draftKey, template, design],
  );

  const isMinimized = useMemo(() => {
    if (!dockState.isMinimized) {
      return false;
    }
    if (!dockState.lastDraftKey || !draftKey) {
      return dockState.isMinimized;
    }
    return dockState.lastDraftKey === draftKey;
  }, [dockState.isMinimized, dockState.lastDraftKey, draftKey]);

  useEffect(() => {
    dispatch(
      setActiveTemplateDraft({
        key: draftKey,
        template: template ?? null,
        design: design ?? null,
      }),
    );
  }, [dispatch, draftKey, template, design]);

  return {
    template: template ?? null,
    design: design ?? null,
    draftKey,
    isLoading,
    error: loadError,
    slots,
    baseDefinition: resolvedBaseDefinition,
    previewDefinition,
    draftEntry,
    availableBlocks,
    addBlock,
    removeBlock,
    moveBlock,
    updateEntry,
    saveDraft,
    resetDraft: resetDraftHandler,
    validateDraft,
    applyToTemplate,
    isMinimized,
    setDockMinimized,
    sections: {
      available: availableSectionKeys,
      visibility: sectionsVisibility,
      toggle: toggleSection,
    },
    style: {
      heroAspectClass,
      heroAspectDefaultRatio,
      setHeroAspectClass,
      bannerAspectClass,
      bannerAspectDefaultRatio,
      setBannerAspectClass,
      cardAspectClass,
      setCardAspectClass,
      itemCardSize: panelTokens?.[ITEM_CARD_SIZE_TOKEN] ?? null,
      setItemCardSize,
    },
    panel: {
      topFiltersVisible: !topFiltersHidden,
      setTopFiltersVisible,
      footerVisible: !footerHidden,
      setFooterVisible: setPanelFooterVisible,
      storeHeroVisible: !storeHeroHidden,
      setStoreHeroVisible,
      topBannerPosition,
      setTopBannerPosition,
    },
  };
};
